// Baukasten der Hörerseite: Welche Blöcke es gibt, was sie an Einstellungen haben
// und wie eine Seite gespeichert wird.
//
// Eine Seite ist eine Liste von Blöcken. Jeder Block hat eine Art (`kind`) und
// Einstellungen (`props`). Gespeichert wird nur geprüftes JSON – niemals HTML aus
// der Datenbank, damit über den Baukasten kein fremder Code auf die Seite kommt.
//
// Neuen Blocktyp ergänzen:
//   1. Hier ein BlockDef mit Schema, Feldern und Standardwerten eintragen.
//   2. In apps/public/src/blocks/ eine Komponente dafür schreiben und in blocks/index.tsx eintragen.
//   3. Fertig – die Suite baut das Formular aus `fields` von selbst.
import { z } from "zod";
// ---------------------------------------------------------------- Bausteine
const channelField = { name: "channel", label: "Kanal", type: "channel", hint: "Leer lassen: alle Kanäle des Radios." };
const linkList = z.array(z.object({
    label: z.string().trim().max(60).default(""),
    url: z.string().trim().max(400).default(""),
    image: z.string().trim().max(400).default(""),
})).max(24).default([]);
const imageList = z.array(z.object({
    url: z.string().trim().max(400),
    alt: z.string().trim().max(200).default(""),
})).max(48).default([]);
const text = (max) => z.string().trim().max(max).default("");
/** Alle Blockarten mit Schema, Formularfeldern und Standardwerten. */
const DEFS = {
    header: {
        label: "Kopf mit Logo",
        hint: "Name, Claim und wahlweise ein Bild ganz oben.",
        group: "Radio",
        once: true,
        fields: [
            { name: "title", label: "Überschrift", type: "text", hint: "Leer: Name des Radios." },
            { name: "claim", label: "Untertitel", type: "text", hint: "Leer: Claim aus dem Branding." },
            { name: "image", label: "Hintergrundbild", type: "image" },
            { name: "show_logo", label: "Logo zeigen", type: "boolean" },
            { name: "style", label: "Darstellung", type: "select", options: [
                    { value: "band", label: "Farbband" }, { value: "bild", label: "Großes Bild" },
                    { value: "schlicht", label: "Schlicht" }, { value: "studio", label: "Senderleiste (jetzt läuft + on air)" },
                ] },
            // Ab hier nur fuer die Senderleiste.
            channelField,
            { name: "show_now", label: "„Jetzt läuft“ zeigen", type: "boolean", hint: "Nur bei Senderleiste." },
            { name: "show_on_air", label: "„On Air“ zeigen", type: "boolean", hint: "Nur bei Senderleiste. Kommt aus dem Sendeplan." },
            { name: "on_air_show", label: "Sendung von Hand", type: "text",
                hint: "Leer: was im Sendeplan steht. Ausgefüllt: gilt stattdessen – für Sondersendungen." },
            { name: "on_air_person", label: "Moderation von Hand", type: "text", hint: "Leer: aus dem Sendeplan." },
            { name: "on_air_photo", label: "Foto von Hand", type: "image", hint: "Leer: Foto aus dem Team." },
            { name: "ausrichtung", label: "Ausrichtung", type: "select", options: [
                    { value: "links", label: "Logo links, Titel daneben" },
                    { value: "mittig", label: "Alles mittig untereinander" },
                ], hint: "Nur bei Senderleiste. Mittig wirkt ruhiger und ist auf breiten Schirmen leichter zu lesen." },
        ],
        schema: z.object({
            title: text(80), claim: text(140), image: text(400),
            show_logo: z.boolean().default(true),
            style: z.enum(["band", "bild", "schlicht", "studio"]).default("band"),
            channel: text(60), show_now: z.boolean().default(true), show_on_air: z.boolean().default(true),
            on_air_show: text(80), on_air_person: text(60), on_air_photo: text(400),
            ausrichtung: z.enum(["links", "mittig"]).default("links"),
        }),
        defaults: { title: "", claim: "", image: "", show_logo: true, style: "band",
            channel: "", show_now: true, show_on_air: true,
            on_air_show: "", on_air_person: "", on_air_photo: "", ausrichtung: "links" },
    },
    player_big: {
        label: "Player groß",
        hint: "Abspielknopf mit Titelanzeige, Lautstärke und Stream-Adressen.",
        group: "Radio",
        fields: [
            channelField,
            { name: "show_next", label: "„Als Nächstes“ zeigen", type: "boolean" },
            { name: "show_history", label: "„Zuletzt gespielt“ zeigen", type: "boolean" },
            { name: "show_streams", label: "Stream-Adressen zeigen", type: "boolean", hint: "Für VLC und Radio-Apps." },
        ],
        schema: z.object({ channel: text(64), show_next: z.boolean().default(true), show_history: z.boolean().default(true), show_streams: z.boolean().default(true) }),
        defaults: { channel: "", show_next: true, show_history: true, show_streams: true },
    },
    player_bar: {
        label: "Player-Leiste",
        hint: "Schmale Leiste, bleibt beim Scrollen unten am Bildschirmrand.",
        group: "Radio",
        once: true,
        fields: [
            channelField,
            { name: "show_cover", label: "Cover zeigen", type: "boolean",
                hint: "Bild des laufenden Titels, sofern eines hinterlegt ist." },
            { name: "show_station", label: "Sendernamen zeigen", type: "boolean",
                hint: "Über dem Titel, wie bei den großen Sendern." },
            { name: "lage", label: "Lage", type: "select", options: [
                    { value: "rand", label: "Am unteren Rand" },
                    { value: "schwebend", label: "Schwebend über dem Inhalt" },
                ], hint: "Schwebend: abgerückt, abgerundet, mit durchscheinendem Hintergrund." },
        ],
        // Aus als Vorgabe: Wer die Leiste schon benutzt, soll sie morgen nicht
        // anders vorfinden. Eingeschaltet wird sie im Baukasten – oder man nimmt
        // gleich die Vorlage „Sender“, die beides mitbringt.
        schema: z.object({ channel: text(64), show_cover: z.boolean().default(false), show_station: z.boolean().default(false),
            lage: z.enum(["rand", "schwebend"]).default("rand") }),
        defaults: { channel: "", show_cover: false, show_station: false, lage: "rand" },
    },
    // Die schmale Zeile ganz oben - ueber allem, auch ueber dem Kopf.
    //
    // Bei den grossen Sendern stehen dort Kurzwege: soziale Netze, Kontakt,
    // Programmzeitschrift. Wetter und Verkehr bewusst NICHT: Das waeren fremde
    // Dienste mit eigenen Daten und eigener Pflege - wer das will, traegt einen
    // Link dorthin ein.
    topbar: {
        label: "Servicezeile oben",
        hint: "Schmale Leiste ganz oben mit Kurzwegen – bleibt beim Scrollen stehen.",
        group: "Service",
        once: true,
        fields: [
            { name: "items", label: "Kurzwege", type: "links" },
            { name: "farbe", label: "Farbe", type: "select", options: [
                    { value: "primary", label: "Markenfarbe" }, { value: "secondary", label: "Dunkel" }, { value: "hell", label: "Hell" },
                ] },
            { name: "sticky", label: "Beim Scrollen stehen lassen", type: "boolean" },
            { name: "verkehr", label: "Verkehr anzeigen", type: "boolean",
                hint: "Ein Auto-Zeichen rechts mit der Zahl der laufenden Meldungen – wie bei den großen Lokalsendern." },
            { name: "verkehr_seite", label: "Seite mit den Meldungen", type: "text",
                placeholder: "verkehr", hint: "Adresse der Seite, auf der der Baustein „Verkehr“ steht." },
            { name: "wetter", label: "Wetter anzeigen", type: "boolean",
                hint: "Temperatur mit Zeichen rechts in der Zeile – Standort und Schalter stehen unter Nachrichten." },
            { name: "wetter_seite", label: "Seite mit dem Wetter", type: "text",
                placeholder: "wetter", hint: "Adresse der Seite, auf der der Baustein „Wetter“ steht." },
            { name: "logo", label: "Logo links zeigen", type: "boolean",
                hint: "Klein, führt zur Startseite – so steht es bei den großen Lokalsendern auf jeder Seite." },
            { name: "menue", label: "Menü", type: "select", options: [
                    { value: "leiste", label: "Eigene Menüleiste darunter" },
                    { value: "burger", label: "Klappmenü hier oben rechts" },
                ] },
        ],
        schema: z.object({
            items: linkList,
            farbe: z.enum(["primary", "secondary", "hell"]).default("primary"),
            sticky: z.boolean().default(true),
            verkehr: z.boolean().default(false),
            verkehr_seite: text(50),
            wetter: z.boolean().default(false),
            wetter_seite: text(50),
            logo: z.boolean().default(true),
            menue: z.enum(["leiste", "burger"]).default("leiste"),
        }),
        defaults: { items: [], farbe: "primary", sticky: true, verkehr: false, verkehr_seite: "verkehr",
            wetter: false, wetter_seite: "wetter", logo: true, menue: "leiste" },
    },
    // Kacheln je Kanal - "Unsere Sender" mit eigenem Abspielknopf. Lohnt sich,
    // sobald ein Radio mehr als einen Kanal hat: Der Hoerer sieht auf einen
    // Blick, was es gibt, und wechselt mit einem Klick.
    channel_tiles: {
        label: "Kanal-Kacheln",
        hint: "Ein Bild je Kanal mit eigenem Abspielknopf – gut für mehrere Programme.",
        group: "Radio",
        fields: [
            { name: "title", label: "Überschrift", type: "text" },
            { name: "columns", label: "Kacheln je Reihe", type: "select", options: [
                    { value: "2", label: "2" }, { value: "3", label: "3" }, { value: "4", label: "4" },
                ] },
            { name: "show_now", label: "Laufenden Titel zeigen", type: "boolean" },
        ],
        schema: z.object({ title: text(80), columns: z.enum(["2", "3", "4"]).default("4"), show_now: z.boolean().default(true) }),
        defaults: { title: "Unsere Sender", columns: "4", show_now: true },
    },
    now_next: {
        label: "Jetzt & Danach",
        hint: "Was gerade läuft und was als Nächstes kommt.",
        group: "Radio",
        fields: [channelField, { name: "title", label: "Überschrift", type: "text" }],
        schema: z.object({ channel: text(64), title: text(80) }),
        defaults: { channel: "", title: "Jetzt im Radio" },
    },
    history: {
        label: "Zuletzt gespielt",
        hint: "Die letzten Titel als Liste.",
        group: "Radio",
        fields: [channelField, { name: "title", label: "Überschrift", type: "text" }, { name: "count", label: "Wie viele Titel", type: "number", min: 3, max: 30 }],
        schema: z.object({ channel: text(64), title: text(80), count: z.number().int().min(3).max(30).default(10) }),
        defaults: { channel: "", title: "Zuletzt gespielt", count: 10 },
    },
    requests: {
        label: "Musikwunsch",
        hint: "Hörer suchen einen Titel und wünschen ihn sich. Erscheint nur, wenn Wünsche eingeschaltet sind.",
        group: "Radio",
        fields: [channelField, { name: "title", label: "Überschrift", type: "text" }],
        schema: z.object({ channel: text(64), title: text(80) }),
        defaults: { channel: "", title: "Musikwunsch" },
    },
    studio_message: {
        label: "Nachricht ins Studio",
        hint: "Text und Sprachnachricht. Erscheint nur, wenn das Studio freigegeben hat.",
        group: "Radio",
        fields: [channelField, { name: "title", label: "Überschrift", type: "text" }],
        schema: z.object({ channel: text(64), title: text(80) }),
        defaults: { channel: "", title: "Nachricht ins Studio" },
    },
    schedule: {
        label: "Sendeplan",
        hint: "Was heute oder diese Woche läuft.",
        group: "Programm",
        fields: [
            { name: "title", label: "Überschrift", type: "text" },
            { name: "mode", label: "Zeitraum", type: "select", options: [{ value: "today", label: "Heute" }, { value: "week", label: "Ganze Woche" }] },
        ],
        schema: z.object({ title: text(80), mode: z.enum(["today", "week"]).default("today") }),
        defaults: { title: "Heute im Programm", mode: "today" },
    },
    shows: {
        label: "Sendungen",
        hint: "Die festen Sendungen des Radios.",
        group: "Programm",
        fields: [{ name: "title", label: "Überschrift", type: "text" }, { name: "limit", label: "Höchstens", type: "number", min: 1, max: 50 }],
        schema: z.object({ title: text(80), limit: z.number().int().min(1).max(50).default(12) }),
        defaults: { title: "Sendungen", limit: 12 },
    },
    team: {
        label: "Team",
        hint: "Das Team als Kartenraster: großes Bild, Funktion, Name – wie bei den grossen Lokalradios.",
        group: "Programm",
        fields: [
            { name: "title", label: "Überschrift", type: "text" },
            { name: "stil", label: "Darstellung", type: "select", options: [
                    { value: "karten", label: "Karten mit grossem Bild" },
                    { value: "liste", label: "Schlichte Liste" },
                ] },
            { name: "spalten", label: "Karten je Reihe", type: "number", min: 2, max: 4,
                hint: "Nur bei Karten. Auf dem Telefon steht ohnehin eine je Reihe." },
            { name: "gruppieren", label: "Nach Gruppe unterteilen", type: "boolean",
                hint: "Eigene Zwischenüberschrift je Gruppe – „Moderation“, „Nachrichtenredaktion“. Die Gruppe steht an der Person." },
            { name: "limit", label: "Höchstens", type: "number", min: 1, max: 50 },
        ],
        schema: z.object({
            title: text(80),
            stil: z.enum(["karten", "liste"]).default("karten"),
            spalten: z.number().int().min(2).max(4).default(3),
            gruppieren: z.boolean().default(false),
            limit: z.number().int().min(1).max(50).default(24),
        }),
        // Karten sind die Vorgabe: Eine Teamseite ohne Gesichter ist eine
        // Adressliste. Wer die alte schlichte Liste will, stellt sie wieder ein.
        defaults: { title: "Moderation", stil: "karten", spalten: 3, gruppieren: false, limit: 24 },
    },
    episodes: {
        label: "Zum Nachhören",
        hint: "Veröffentlichte Podcast-Folgen mit Abspieler.",
        group: "Programm",
        fields: [{ name: "title", label: "Überschrift", type: "text" }, { name: "limit", label: "Höchstens", type: "number", min: 1, max: 50 }],
        schema: z.object({ title: text(80), limit: z.number().int().min(1).max(50).default(9) }),
        defaults: { title: "Zum Nachhören", limit: 9 },
    },
    text: {
        label: "Text & Bild",
        hint: "Freier Abschnitt. Fett, kursiv, Listen, Links und Zwischenüberschriften.",
        group: "Inhalt",
        fields: [
            { name: "title", label: "Überschrift", type: "text" },
            { name: "body", label: "Text", type: "markdown", hint: "**fett**, *kursiv*, ## Zwischenüberschrift, - Liste, [Link](https://…)" },
            { name: "image", label: "Bild", type: "image" },
            { name: "image_side", label: "Bild steht", type: "select", options: [{ value: "left", label: "links" }, { value: "right", label: "rechts" }, { value: "top", label: "oben" }] },
        ],
        schema: z.object({ title: text(120), body: text(20_000), image: text(400), image_side: z.enum(["left", "right", "top"]).default("right") }),
        defaults: { title: "", body: "", image: "", image_side: "right" },
    },
    gallery: {
        label: "Galerie",
        hint: "Bilder nebeneinander, z. B. von einer Veranstaltung.",
        group: "Inhalt",
        fields: [
            { name: "title", label: "Überschrift", type: "text" },
            { name: "images", label: "Bilder", type: "images" },
            { name: "columns", label: "Spalten", type: "number", min: 2, max: 5 },
        ],
        schema: z.object({ title: text(120), images: imageList, columns: z.number().int().min(2).max(5).default(3) }),
        defaults: { title: "", images: [], columns: 3 },
    },
    video: {
        label: "Video",
        hint: "YouTube, Vimeo oder eigene Datei. Wird erst nach einem Klick geladen – vorher geht keine Verbindung dorthin.",
        group: "Inhalt",
        fields: [
            { name: "title", label: "Überschrift", type: "text" },
            { name: "url", label: "Adresse des Videos", type: "text", placeholder: "https://www.youtube.com/watch?v=…" },
            { name: "poster", label: "Vorschaubild", type: "image", hint: "Ohne Bild erscheint eine schlichte Fläche." },
        ],
        schema: z.object({ title: text(120), url: text(400), poster: text(400) }),
        defaults: { title: "", url: "", poster: "" },
    },
    news_ticker: {
        label: "Nachrichten-Ticker",
        hint: "Die Meldungen aus eurem Nachrichtenteil.",
        group: "Inhalt",
        fields: [
            { name: "title", label: "Überschrift", type: "text" },
            { name: "area", label: "Bereich", type: "select", options: [
                    { value: "region", label: "Region" }, { value: "germany", label: "Deutschland" }, { value: "world", label: "Welt" },
                ] },
            { name: "count", label: "Wie viele Meldungen", type: "number", min: 1, max: 20 },
            { name: "style", label: "Darstellung", type: "select", options: [{ value: "kasten", label: "Kasten" }, { value: "band", label: "Laufband" }] },
        ],
        schema: z.object({ title: text(80), area: z.enum(["region", "germany", "world"]).default("region"), count: z.number().int().min(1).max(20).default(5), style: z.enum(["kasten", "band"]).default("kasten") }),
        defaults: { title: "Aus der Region", area: "region", count: 5, style: "kasten" },
    },
    verkehr: {
        label: "Verkehr",
        hint: "Staus, Sperrungen und Baustellen auf euren Autobahnen – von der Autobahn GmbH, laufend aktuell.",
        group: "Inhalt",
        fields: [
            { name: "title", label: "Überschrift", type: "text" },
            { name: "count", label: "Wie viele Meldungen", type: "number", min: 1, max: 12 },
            { name: "style", label: "Darstellung", type: "select", options: [
                    { value: "kasten", label: "Kasten mit Liste" },
                    { value: "zeile", label: "Eine Zeile (für ganz oben)" },
                ] },
            { name: "leer_zeigen", label: "Auch „freie Fahrt“ zeigen", type: "boolean",
                hint: "Aus: Gibt es keine Meldung, bleibt der Block ganz weg." },
        ],
        schema: z.object({
            title: text(80),
            count: z.number().int().min(1).max(12).default(4),
            style: z.enum(["kasten", "zeile"]).default("kasten"),
            leer_zeigen: z.boolean().default(true),
        }),
        defaults: { title: "Verkehr", count: 4, style: "kasten", leer_zeigen: true },
    },
    wetter: {
        label: "Wetter",
        hint: "Wie es gerade ist und wie die nächsten Tage werden – vom Deutschen Wetterdienst.",
        group: "Inhalt",
        fields: [
            { name: "title", label: "Überschrift", type: "text" },
            { name: "tage", label: "Wie viele Tage", type: "number", min: 0, max: 4,
                hint: "0: nur, wie es gerade ist." },
            { name: "style", label: "Darstellung", type: "select", options: [
                    { value: "kasten", label: "Kasten mit Tagen" },
                    { value: "zeile", label: "Eine Zeile" },
                ] },
        ],
        schema: z.object({
            title: text(80),
            tage: z.number().int().min(0).max(4).default(3),
            style: z.enum(["kasten", "zeile"]).default("kasten"),
        }),
        defaults: { title: "Wetter", tage: 3, style: "kasten" },
    },
    countdown: {
        label: "Countdown",
        hint: "Zählt bis zu einem Zeitpunkt herunter, z. B. bis zur nächsten Sendung.",
        group: "Inhalt",
        fields: [
            { name: "title", label: "Überschrift", type: "text" },
            { name: "target", label: "Zeitpunkt", type: "datetime" },
            { name: "note", label: "Text danach", type: "text", hint: "Steht dort, wenn der Zeitpunkt vorbei ist." },
        ],
        schema: z.object({ title: text(120), target: text(40), note: text(160) }),
        defaults: { title: "Nächste Sendung", target: "", note: "Es geht los!" },
    },
    sponsors: {
        label: "Sponsoren",
        hint: "Logos mit Link, in einer Reihe.",
        group: "Service",
        fields: [{ name: "title", label: "Überschrift", type: "text" }, { name: "items", label: "Logos", type: "links" }],
        schema: z.object({ title: text(120), items: linkList }),
        defaults: { title: "Unterstützt von", items: [] },
    },
    contact: {
        label: "Kontaktformular",
        hint: "Nachrichten landen in der Suite unter Aussehen → Kontakt. Ohne fremde Dienste.",
        group: "Service",
        fields: [
            { name: "title", label: "Überschrift", type: "text" },
            { name: "intro", label: "Einleitung", type: "textarea" },
            { name: "subjects", label: "Anliegen zur Auswahl", type: "textarea",
                hint: "Eines je Zeile, z. B. „Bewerbung als Moderator/in“. Leer: ein freies Betreff-Feld wie bisher." },
        ],
        schema: z.object({ title: text(120), intro: text(600), subjects: text(400) }),
        defaults: { title: "Kontakt", intro: "Schreib uns – wir melden uns.", subjects: "" },
    },
    legal: {
        label: "Rechtliches",
        hint: "Impressum und Datenschutz aus euren Angaben unter Aussehen → Rechtliches.",
        group: "Service",
        fields: [{ name: "part", label: "Teil", type: "select", options: [
                    { value: "impressum", label: "Impressum" }, { value: "datenschutz", label: "Datenschutz" }, { value: "beides", label: "Beides" },
                ] }],
        schema: z.object({ part: z.enum(["impressum", "datenschutz", "beides"]).default("beides") }),
        defaults: { part: "beides" },
    },
    embed_hint: {
        label: "Player zum Einbauen",
        hint: "Zeigt Hörern den Code, mit dem sie euren Player auf ihre Seite holen.",
        group: "Service",
        once: true,
        fields: [{ name: "title", label: "Überschrift", type: "text" }],
        schema: z.object({ title: text(120) }),
        defaults: { title: "Player auf der eigenen Website einbauen" },
    },
    links: {
        label: "Link-Reihe",
        hint: "Knöpfe zu anderen Seiten, z. B. soziale Netze oder Spenden.",
        group: "Service",
        fields: [{ name: "title", label: "Überschrift", type: "text" }, { name: "items", label: "Links", type: "links" }],
        schema: z.object({ title: text(120), items: linkList }),
        defaults: { title: "", items: [] },
    },
    posts: {
        label: "Neueste Beiträge",
        hint: "Beiträge aus der Redaktion – alle oder nur eine Kategorie.",
        group: "Redaktion",
        fields: [
            { name: "title", label: "Überschrift", type: "text" },
            { name: "category", label: "Kategorie", type: "category", hint: "Leer lassen: alle Kategorien." },
            { name: "count", label: "Wie viele Beiträge", type: "number", min: 1, max: 24 },
            { name: "layout", label: "Darstellung", type: "select", options: [{ value: "raster", label: "Raster mit Bild" }, { value: "liste", label: "Liste" }] },
            { name: "show_more", label: "Link „Alle Beiträge“ zeigen", type: "boolean" },
        ],
        schema: z.object({
            title: text(80),
            category: text(60),
            count: z.number().int().min(1).max(24).default(6),
            layout: z.enum(["raster", "liste"]).default("raster"),
            show_more: z.boolean().default(true),
        }),
        defaults: { title: "Neues aus der Redaktion", category: "", count: 6, layout: "raster", show_more: true },
    },
    post_featured: {
        label: "Beitrag hervorheben",
        hint: "Ein bestimmter Beitrag groß, z. B. die Ankündigung der nächsten Sendung.",
        group: "Redaktion",
        fields: [
            { name: "post", label: "Beitrag", type: "post" },
            { name: "title", label: "Überschrift darüber", type: "text", hint: "Leer lassen: keine Überschrift." },
        ],
        schema: z.object({ post: text(80), title: text(80) }),
        defaults: { post: "", title: "" },
    },
    quote: {
        label: "Zitat",
        hint: "Ein hervorgehobener Satz, z. B. aus einem Interview.",
        group: "Inhalt",
        fields: [
            { name: "text", label: "Zitat", type: "textarea" },
            { name: "source", label: "Wer hat es gesagt", type: "text" },
        ],
        schema: z.object({ text: text(600), source: text(120) }),
        defaults: { text: "", source: "" },
    },
    episode: {
        label: "Folge zum Nachhören",
        hint: "Eine einzelne Podcast-Folge mit Abspieler, passend zum Beitrag.",
        group: "Programm",
        fields: [
            { name: "episode", label: "Folge", type: "episode" },
            { name: "title", label: "Überschrift", type: "text" },
        ],
        schema: z.object({ episode: text(80), title: text(80) }),
        defaults: { episode: "", title: "Zum Nachhören" },
    },
    divider: {
        label: "Trennlinie",
        hint: "Ein wenig Luft zwischen zwei Blöcken.",
        group: "Inhalt",
        fields: [],
        schema: z.object({}),
        defaults: {},
    },
    poll: {
        label: "Live-Abstimmung",
        hint: "Die laufende Frage aus dem Studio. Steht keine an, bleibt der Block still.",
        group: "Radio",
        fields: [channelField, { name: "title", label: "Überschrift", type: "text" }],
        schema: z.object({ channel: text(64), title: text(80) }),
        defaults: { channel: "", title: "Jetzt abstimmen" },
    },
    quiz: {
        label: "Mitraten",
        hint: "Die Quizfrage aus der Sendung, mit Auflösung und – wenn gewünscht – Bestenliste.",
        group: "Radio",
        fields: [channelField, { name: "title", label: "Überschrift", type: "text" }],
        schema: z.object({ channel: text(64), title: text(80) }),
        defaults: { channel: "", title: "Mitraten" },
    },
    // ---- Mitmach-Module (nur sichtbar, wenn das Modul eingeschaltet ist) ----
    greetings: {
        label: "Grußwand",
        hint: "Freigegebene Grüße der Hörer. Braucht das Modul „Grußwand“.",
        group: "Radio",
        fields: [
            channelField,
            { name: "title", label: "Überschrift", type: "text" },
            { name: "count", label: "Wie viele Grüße", type: "number", min: 3, max: 60 },
            { name: "layout", label: "Darstellung", type: "select", options: [
                    { value: "kacheln", label: "Kacheln" }, { value: "liste", label: "Liste" },
                ] },
            { name: "show_place", label: "Ort zeigen", type: "boolean", hint: "Aus: nur der Name, ohne „aus Prüm“." },
            { name: "show_count", label: "Anzahl zeigen", type: "boolean" },
        ],
        schema: z.object({
            channel: text(64),
            title: text(80),
            count: z.number().int().min(3).max(60).default(12),
            layout: z.enum(["kacheln", "liste"]).default("kacheln"),
            show_place: z.boolean().default(true),
            show_count: z.boolean().default(true),
        }),
        defaults: { channel: "", title: "Grußwand", count: 12, layout: "kacheln", show_place: true, show_count: true },
    },
    rating: {
        label: "Titel bewerten",
        hint: "Daumen hoch oder runter am laufenden Titel. Braucht das Modul „Titel bewerten“.",
        group: "Radio",
        fields: [
            channelField,
            { name: "title", label: "Überschrift", type: "text" },
            { name: "show_score", label: "Stand zeigen", type: "boolean", hint: "Aus: nur Danke, ohne Zahlen." },
        ],
        schema: z.object({ channel: text(64), title: text(80), show_score: z.boolean().default(true) }),
        defaults: { channel: "", title: "Wie findest du den Titel?", show_score: true },
    },
    charts: {
        label: "Hörer-Charts",
        hint: "Die Top-Liste aus Wünschen und Bewertungen. Braucht das Modul „Hörer-Charts“.",
        group: "Radio",
        fields: [
            channelField,
            { name: "title", label: "Überschrift", type: "text" },
            { name: "count", label: "Wie viele Plätze", type: "number", min: 3, max: 50 },
            { name: "show_move", label: "Bewegung zur Vorwoche zeigen", type: "boolean" },
        ],
        schema: z.object({
            channel: text(64),
            title: text(80),
            count: z.number().int().min(3).max(50).default(10),
            show_move: z.boolean().default(true),
        }),
        defaults: { channel: "", title: "Hörer-Charts", count: 10, show_move: true },
    },
    contest: {
        label: "Gewinnspiel",
        hint: "Das laufende Gewinnspiel mit Formular. Braucht das Modul „Gewinnspiel“.",
        group: "Radio",
        fields: [
            channelField,
            { name: "title", label: "Überschrift", type: "text" },
            { name: "show_count", label: "Anzahl der Teilnahmen zeigen", type: "boolean", hint: "Nur die Zahl – nie ein Name." },
            { name: "terms_page", label: "Teilnahmebedingungen", type: "select", options: [
                    { value: "aufklappbar", label: "zum Aufklappen im Block" }, { value: "seite", label: "als eigene Unterseite verlinken" },
                ] },
        ],
        schema: z.object({
            channel: text(64),
            title: text(80),
            show_count: z.boolean().default(true),
            terms_page: z.enum(["aufklappbar", "seite"]).default("aufklappbar"),
        }),
        defaults: { channel: "", title: "Gewinnspiel", show_count: true, terms_page: "aufklappbar" },
    },
    voice_survey: {
        label: "Frage der Woche",
        hint: "Die laufende Frage mit Aufnahmeknopf. Braucht das Modul „Frage der Woche“.",
        group: "Radio",
        fields: [
            channelField,
            { name: "title", label: "Überschrift", type: "text" },
            { name: "intro", label: "Text über dem Knopf", type: "textarea", hint: "Leer: ein kurzer Standardsatz." },
            { name: "show_answers", label: "Freigegebene Antworten zeigen", type: "boolean",
                hint: "Wirkt nur, wenn im Modul auch „Antworten öffentlich zeigen“ an ist." },
        ],
        schema: z.object({
            channel: text(64),
            title: text(80),
            intro: text(400),
            show_answers: z.boolean().default(true),
        }),
        defaults: { channel: "", title: "Frage der Woche", intro: "", show_answers: true },
    },
    chat: {
        label: "Hörerchat",
        hint: "Der Chatraum des Kanals: Hörer und Moderation schreiben miteinander. Braucht das Modul „Hörerchat“.",
        group: "Radio",
        fields: [
            channelField,
            { name: "title", label: "Überschrift", type: "text" },
            { name: "hoehe", label: "Höhe", type: "select", options: [
                    { value: "klein", label: "Klein (300 px)" }, { value: "mittel", label: "Mittel (460 px)" }, { value: "gross", label: "Groß (640 px)" },
                ] },
            { name: "show_users", label: "Wer ist da? zeigen", type: "boolean", hint: "Die Liste neben dem Raum. Auf dem Handy steht sie über dem Raum." },
        ],
        schema: z.object({
            channel: text(64),
            title: text(80),
            hoehe: z.enum(["klein", "mittel", "gross"]).default("mittel"),
            show_users: z.boolean().default(true),
        }),
        defaults: { channel: "", title: "Chat", hoehe: "mittel", show_users: true },
    },
    phone: {
        label: "Hörertelefon",
        hint: "Knopf zum Anrufen, mit Warteschlange. Braucht das Modul „Hörertelefon“.",
        group: "Radio",
        fields: [
            channelField,
            { name: "title", label: "Überschrift", type: "text" },
            { name: "intro", label: "Einleitung", type: "textarea", hint: "Ein, zwei Sätze über dem Knopf. Leer: Standardtext." },
            { name: "ask_topic", label: "Nach dem Anliegen fragen", type: "boolean", hint: "Ein Stichwort, damit das Studio weiß, worum es geht." },
        ],
        schema: z.object({
            channel: text(64),
            title: text(80),
            intro: text(300),
            ask_topic: z.boolean().default(true),
        }),
        defaults: { channel: "", title: "Ruf uns an", intro: "", ask_topic: true },
    },
    reminders: {
        label: "Erinnerungen",
        hint: "Hörer lassen sich an eine Sendung erinnern – ohne App und ohne Konto. Braucht das Modul „Erinnerungen“.",
        group: "Radio",
        fields: [
            channelField,
            { name: "title", label: "Überschrift", type: "text" },
            { name: "count", label: "Wie viele Sendungen zeigen", type: "number", min: 1, max: 20 },
            { name: "show_calendar", label: "Kalender anbieten", type: "boolean", hint: "Geht immer – auch ohne erlaubte Mitteilungen." },
            { name: "note", label: "Eigener Hinweis", type: "textarea", hint: "Steht über der Liste. Leer: der Standardtext." },
        ],
        schema: z.object({
            channel: text(64),
            title: text(80),
            count: z.number().int().min(1).max(20).default(6),
            show_calendar: z.boolean().default(true),
            note: text(300),
        }),
        defaults: { channel: "", title: "Erinnere mich", count: 6, show_calendar: true, note: "" },
    },
    events: {
        label: "Termine",
        hint: "Die freigegebenen Termine aus der Gegend, nach Tagen sortiert. Braucht das Modul „Mitmach-Kalender“.",
        group: "Inhalt",
        fields: [
            { name: "title", label: "Überschrift", type: "text" },
            { name: "days", label: "Wie weit voraus zeigen (Tage)", type: "number", min: 1, max: 365, hint: "Leer oder 0: so weit, wie es im Modul eingestellt ist." },
            { name: "count", label: "Höchstens so viele Termine", type: "number", min: 3, max: 200 },
            { name: "show_filter", label: "Filter nach Ort und Kategorie zeigen", type: "boolean" },
            { name: "show_ics", label: "„In meinen Kalender“ anbieten", type: "boolean", hint: "Ein Knopf je Termin und ein Abo-Link für alle." },
            { name: "empty_text", label: "Text, wenn nichts ansteht", type: "text" },
        ],
        schema: z.object({
            title: text(80),
            days: z.number().int().min(0).max(365).default(0),
            count: z.number().int().min(3).max(200).default(40),
            show_filter: z.boolean().default(true),
            show_ics: z.boolean().default(true),
            empty_text: text(160),
        }),
        defaults: { title: "Termine", days: 0, count: 40, show_filter: true, show_ics: true, empty_text: "Gerade steht kein Termin an." },
    },
    event_form: {
        label: "Termine einreichen",
        hint: "Formular für Hörer: Was, wann, wo. Die Redaktion gibt frei. Braucht das Modul „Mitmach-Kalender“.",
        group: "Inhalt",
        fields: [
            { name: "title", label: "Überschrift", type: "text" },
            { name: "intro", label: "Text über dem Formular", type: "textarea", hint: "Leer: der Hinweis zur Region aus den Einstellungen des Moduls." },
            { name: "ask_category", label: "Nach der Kategorie fragen", type: "boolean" },
        ],
        schema: z.object({ title: text(80), intro: text(400), ask_category: z.boolean().default(true) }),
        defaults: { title: "Termin einreichen", intro: "", ask_category: true },
    },
    // Ans Ende gehängt, damit parallele Zweige sich hier nicht in die Quere kommen.
    veranstaltung_form: {
        label: "Feier anfragen",
        hint: "Formular für eine private Feier: Termin, Art, Personenzahl, Wünsche. Daraus wird eine Anfrage, keine Buchung. Braucht das Modul „Private Veranstaltungen“.",
        group: "Service",
        fields: [
            { name: "title", label: "Überschrift", type: "text" },
            { name: "intro", label: "Text über dem Formular", type: "textarea", hint: "Leer: der Hinweis aus den Einstellungen des Moduls." },
            { name: "days", label: "Freie Termine zeigen für (Tage)", type: "number", min: 7, max: 365 },
            { name: "show_busy", label: "Schon vergebene Tage auflisten", type: "boolean", hint: "Eine Liste zum Aufklappen, damit niemand zweimal denselben Abend anfragt." },
            { name: "note", label: "Text unter dem Formular", type: "textarea" },
        ],
        schema: z.object({
            title: text(80),
            intro: text(600),
            days: z.number().int().min(7).max(365).default(120),
            show_busy: z.boolean().default(true),
            note: text(400),
        }),
        defaults: {
            title: "Feier anfragen",
            intro: "",
            days: 120,
            show_busy: true,
            note: "Das ist eine Anfrage, noch keine Buchung. Wir melden uns und sagen zu oder ab.",
        },
    },
};
export const BLOCK_KINDS = Object.keys(DEFS);
export const BLOCKS = Object.fromEntries(Object.entries(DEFS).map(([kind, def]) => [kind, { ...def, kind }]));
export function isBlockKind(value) {
    return typeof value === "string" && Object.prototype.hasOwnProperty.call(DEFS, value);
}
const RawBlock = z.object({
    id: z.string().trim().min(1).max(40).optional(),
    kind: z.string(),
    props: z.record(z.unknown()).default({}),
});
let counter = 0;
export function blockId() {
    counter = (counter + 1) % 100_000;
    return `b${Date.now().toString(36)}${counter.toString(36)}`;
}
/** Neuer Block mit den Standardwerten seiner Art. */
export function newBlock(kind) {
    return { id: blockId(), kind, props: { ...BLOCKS[kind].defaults } };
}
/**
 * Prüft eine gespeicherte oder geschickte Bausteinliste.
 * Unbekannte Arten fallen still weg: So bleibt eine Seite lesbar, auch wenn sie
 * mit einer neueren Version gebaut wurde, die einen Block mehr kennt.
 */
export function parseBlocks(value) {
    const raw = z.array(RawBlock).max(80).safeParse(value);
    if (!raw.success)
        return [];
    const out = [];
    for (const b of raw.data) {
        if (!isBlockKind(b.kind))
            continue;
        const props = BLOCKS[b.kind].schema.safeParse(b.props);
        out.push({ id: b.id ?? blockId(), kind: b.kind, props: props.success ? props.data : { ...BLOCKS[b.kind].defaults } });
    }
    return out;
}
/** Wie parseBlocks, meldet aber, was nicht passte (für Fehlermeldungen beim Speichern). */
export function checkBlocks(value) {
    const raw = z.array(RawBlock).max(80).safeParse(value);
    if (!raw.success)
        return { ok: false, error: "Die Seite konnte nicht gelesen werden." };
    const blocks = [];
    for (const b of raw.data) {
        if (!isBlockKind(b.kind))
            return { ok: false, error: `Unbekannter Block „${b.kind}“.` };
        const parsed = BLOCKS[b.kind].schema.safeParse(b.props);
        if (!parsed.success)
            return { ok: false, error: `Block „${BLOCKS[b.kind].label}“: ${parsed.error.issues[0]?.message ?? "Eingabe passt nicht."}` };
        blocks.push({ id: b.id ?? blockId(), kind: b.kind, props: parsed.data });
    }
    return { ok: true, blocks };
}
// ---------------------------------------------------------------- Aussehen
/**
 * Schriftpaare für die Hörerseite.
 *
 * Bewusst ohne Google Fonts: Ein Link dorthin schickt die Adresse jedes Hörers an
 * einen fremden Server. Das braucht in Deutschland eine Einwilligung und passt
 * nicht zu einer Seite, die ohne Cookies und ohne Zähldienste auskommen soll.
 * Eigene Schriftdateien mitzuliefern kostet Ladezeit und Pflege; deshalb sind die
 * Paare aus Schriften gebaut, die auf Windows, macOS, Android und Linux ohnehin
 * da sind. Wer eigene Schriften will, legt sie selbst ab – siehe OPERATIONS.md.
 */
export const FONT_SETS = {
    system: {
        label: "Systemschrift (schnell, überall gleich)",
        heading: `ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`,
        body: `ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`,
    },
    klassisch: {
        label: "Klassisch (Serifen-Überschriften)",
        heading: `Georgia, "Iowan Old Style", "Times New Roman", serif`,
        body: `ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`,
    },
    kraeftig: {
        label: "Kräftig (breite Überschriften)",
        heading: `"Arial Black", "Helvetica Neue", Impact, system-ui, sans-serif`,
        body: `"Helvetica Neue", Helvetica, Arial, system-ui, sans-serif`,
    },
    ruhig: {
        label: "Ruhig (Serifen-Text)",
        heading: `"Trebuchet MS", "Segoe UI", system-ui, sans-serif`,
        body: `Georgia, "Iowan Old Style", "Times New Roman", serif`,
    },
};
/** Eckenrundung der Kästen; „weich“ ist die Rundung der bisherigen Hörerseite. */
export const RADIUS = { weich: "10px", mittel: "5px", eckig: "0px" };
export const DENSITY = { luftig: 1.25, normal: 1, kompakt: 0.8 };
export const DEFAULT_SITE_SETTINGS = {
    font_set: "system",
    radius: "weich",
    density: "normal",
    matomo_url: "",
    matomo_site_id: "",
    legal: { operator: "", address: "", contact: "", responsible: "", privacy_contact: "", extra: "" },
};
export const SiteSettingsSchema = z.object({
    font_set: z.enum(Object.keys(FONT_SETS)).default("system"),
    radius: z.enum(Object.keys(RADIUS)).default("weich"),
    density: z.enum(Object.keys(DENSITY)).default("normal"),
    matomo_url: z.string().trim().max(300).default(""),
    matomo_site_id: z.string().trim().max(20).default(""),
    legal: z.object({
        operator: z.string().trim().max(200).default(""),
        address: z.string().trim().max(400).default(""),
        contact: z.string().trim().max(200).default(""),
        responsible: z.string().trim().max(200).default(""),
        privacy_contact: z.string().trim().max(200).default(""),
        extra: z.string().trim().max(4000).default(""),
    }).default(DEFAULT_SITE_SETTINGS.legal),
});
const legalPage = {
    slug: "rechtliches", title: "Impressum & Datenschutz", menu_label: "Rechtliches", in_menu: true,
    blocks: [{ kind: "legal", props: { part: "beides" } }],
};
const contactPage = {
    slug: "kontakt", title: "Kontakt", in_menu: true,
    blocks: [{ kind: "contact" }],
};
/**
 * „Klassisch“ ist genau die Seite, die es vor dem Baukasten gab – wer nichts
 * umbaut, sieht dieselbe Seite wie bisher.
 */
export const CLASSIC_HOME_BLOCKS = [
    { kind: "header" },
    { kind: "player_big" },
    { kind: "requests" },
    { kind: "studio_message" },
    { kind: "schedule", props: { title: "Heute im Programm", mode: "today" } },
    { kind: "shows" },
    { kind: "team" },
    { kind: "episodes" },
    { kind: "embed_hint" },
];
export const TEMPLATES = [
    {
        key: "klassisch",
        label: "Klassisch",
        hint: "Wie die bisherige Hörerseite: Player oben, darunter Wünsche, Programm, Team und Folgen.",
        pages: [
            { slug: "start", title: "Start", in_menu: true, is_home: true, blocks: CLASSIC_HOME_BLOCKS },
            { slug: "sendungen", title: "Sendungen", in_menu: true, blocks: [{ kind: "shows" }, { kind: "schedule", props: { mode: "week", title: "Der Wochenplan" } }] },
            { slug: "team", title: "Team", in_menu: true, blocks: [{ kind: "team" }] },
            contactPage,
            legalPage,
        ],
    },
    {
        key: "sender",
        label: "Sender",
        hint: "Wie die großen Lokalradios: oben die Senderleiste mit „Jetzt läuft“ und „On Air“, "
            + "darunter Nachrichten und die Kanäle, unten die Player-Leiste mit Cover.",
        pages: [
            {
                slug: "start", title: "Start", in_menu: true, is_home: true,
                blocks: [
                    // Die Senderleiste steht ausserhalb von <main> und bleibt oben stehen.
                    { kind: "topbar", props: { farbe: "primary", sticky: true, items: [] } },
                    { kind: "header", props: { style: "studio", show_logo: true, show_now: true, show_on_air: true } },
                    { kind: "posts", props: { title: "Aktuelle Nachrichten", limit: 6 } },
                    { kind: "channel_tiles", props: { title: "Unsere Sender", columns: "4", show_now: true } },
                    { kind: "schedule", props: { title: "Heute im Programm", mode: "today" } },
                    { kind: "episodes", props: { title: "Zum Nachhören", limit: 6 } },
                    // Mit Cover und Sendernamen - hier ist es gewollt.
                    { kind: "player_bar", props: { show_cover: true, show_station: true, lage: "schwebend" } },
                ],
            },
            { slug: "sendungen", title: "Sendungen", in_menu: true, blocks: [{ kind: "shows" }, { kind: "schedule", props: { mode: "week", title: "Der Wochenplan" } }] },
            { slug: "team", title: "Team", in_menu: true, blocks: [{ kind: "team" }] },
            contactPage,
            legalPage,
        ],
    },
    {
        key: "magazin",
        label: "Magazin",
        hint: "Für Radios mit Redaktion: großes Bild, Nachrichten aus der Region, Text und Galerie.",
        pages: [
            {
                slug: "start", title: "Start", in_menu: true, is_home: true,
                blocks: [
                    { kind: "header", props: { style: "bild", show_logo: true } },
                    { kind: "player_big", props: { show_history: false } },
                    { kind: "news_ticker", props: { title: "Aus der Region", area: "region", count: 5, style: "kasten" } },
                    { kind: "text", props: { title: "Willkommen", body: "Hier steht, wofür euer Radio steht.\n\nDiesen Text ändert ihr in der Suite unter Aussehen." } },
                    { kind: "schedule", props: { title: "Heute im Programm", mode: "today" } },
                    { kind: "episodes", props: { title: "Zum Nachhören", limit: 6 } },
                    { kind: "player_bar" },
                ],
            },
            { slug: "sendungen", title: "Sendungen", in_menu: true, blocks: [{ kind: "shows" }, { kind: "schedule", props: { mode: "week", title: "Der Wochenplan" } }, { kind: "team" }] },
            { slug: "mitmachen", title: "Mitmachen", in_menu: true, blocks: [{ kind: "requests" }, { kind: "studio_message" }, { kind: "contact" }] },
            legalPage,
        ],
    },
    {
        key: "hobbyradio",
        label: "Hobbyradio",
        hint: "Wie bei den kleinen Szene-Radios: Wunschbox, Chat, Grußwand, DJ-Team und Partnerradios – "
            + "alles dicht gepackt auf einer Seite, plus eigene Seiten für Sendeplan, Team und Bewerbung.",
        // Kompakt und mit weichen Ecken: Diese Seiten leben davon, dass viel auf
        // einen Bildschirm passt. Die Schrift bleibt die des Systems – sie lädt
        // nichts nach und sieht auf jedem Rechner gleich aus.
        look: { density: "kompakt", radius: "weich", font_set: "system" },
        pages: [
            {
                slug: "start", title: "Start", in_menu: true, is_home: true,
                blocks: [
                    // Oben die Kurzwege, die auf diesen Seiten immer da sind.
                    // Kurzwege bleiben leer: Die Adressen tragen den Namen des Radios
                    // („/meinradio/wunschbox"), und den kennt eine Vorlage nicht. Die
                    // Seiten stehen ohnehin im Menü darunter.
                    { kind: "topbar", props: { farbe: "primary", sticky: true, logo: true, menue: "leiste", items: [] } },
                    // „Jetzt auf Sendung": Logo, was läuft, wer sendet – mit Foto.
                    { kind: "header", props: { style: "studio", show_logo: true, show_now: true, show_on_air: true } },
                    // Der Player mit den Stream-Adressen: Bei diesen Radios hört die
                    // halbe Hörerschaft mit Winamp oder VLC, nicht im Browser.
                    { kind: "player_big", props: { show_next: true, show_history: true, show_streams: true } },
                    // Die Wunschbox – das Herzstück dieser Seiten.
                    { kind: "requests", props: { title: "Wunschbox" } },
                    { kind: "studio_message", props: { title: "Gruß ins Studio" } },
                    { kind: "chat", props: { title: "Chat", hoehe: "mittel", show_users: true } },
                    { kind: "greetings", props: { title: "Grußwand", count: 12, layout: "kacheln" } },
                    { kind: "schedule", props: { title: "Wer ist als Nächstes dran?", mode: "today" } },
                    { kind: "team", props: { title: "Unser DJ-Team", stil: "karten", spalten: 4, limit: 24 } },
                    { kind: "charts", props: { title: "Eure Top 10", count: 10 } },
                    { kind: "history", props: { title: "Zuletzt gespielt", count: 10 } },
                    // Partnerradios: bei diesen Sendern der halbe Seiteninhalt – man
                    // verlinkt sich gegenseitig, mit Banner.
                    { kind: "links", props: { title: "Partnerradios", items: [] } },
                    { kind: "sponsors", props: { title: "Unsere Partner", items: [] } },
                    { kind: "text", props: { title: "Sprich mit uns",
                            body: "Wir sind nicht nur im Radio zu hören: Im Chat ist immer jemand da, und wer lieber redet, kommt auf unseren TeamSpeak- oder Discord-Server.\n\nDie Adresse tragt ihr hier ein – in der Suite unter *Aussehen → Seiten bauen*." } },
                    { kind: "player_bar", props: { show_cover: true, show_station: true, lage: "schwebend" } },
                ],
            },
            {
                slug: "sendeplan", title: "Sendeplan", in_menu: true,
                blocks: [
                    { kind: "schedule", props: { title: "Der Wochenplan", mode: "week" } },
                    { kind: "shows", props: {} },
                    { kind: "player_bar", props: { show_cover: true, lage: "schwebend" } },
                ],
            },
            {
                slug: "team", title: "Team", in_menu: true,
                blocks: [
                    { kind: "team", props: { title: "Unser DJ-Team", stil: "karten", spalten: 4, gruppieren: true, limit: 50 } },
                    { kind: "text", props: { title: "Wir suchen Verstärkung",
                            body: "Du legst gern auf oder moderierst? Melde dich über die Seite *DJ werden* – Vorerfahrung ist keine Bedingung, Lust schon." } },
                ],
            },
            {
                slug: "wunschbox", title: "Wunschbox", in_menu: true,
                blocks: [
                    { kind: "requests", props: { title: "Dein Musikwunsch" } },
                    { kind: "studio_message", props: { title: "Gruß ins Studio" } },
                    { kind: "greetings", props: { title: "Was andere geschrieben haben", count: 24, layout: "liste" } },
                    { kind: "player_bar", props: { lage: "schwebend" } },
                ],
            },
            {
                slug: "bewerben", title: "DJ werden", in_menu: true,
                blocks: [
                    { kind: "text", props: { title: "Du willst zu uns?",
                            body: "Schreib uns, was du spielen willst und wann du Zeit hast. Wir melden uns und zeigen dir das Studio." } },
                    { kind: "contact", props: { title: "Bewerbung", intro: "Wir lesen jede Zuschrift.",
                            subjects: "Bewerbung als DJ\nBewerbung als Moderator/in\nBewerbung fürs Technik-Team\nEtwas anderes" } },
                ],
            },
            legalPage,
        ],
    },
    {
        key: "minimal",
        label: "Minimal",
        hint: "Nur das Nötigste: hören, wünschen, Impressum.",
        pages: [
            {
                slug: "start", title: "Start", in_menu: true, is_home: true,
                blocks: [
                    { kind: "header", props: { style: "schlicht" } },
                    { kind: "player_big", props: { show_history: false, show_streams: true } },
                    { kind: "requests" },
                ],
            },
            legalPage,
        ],
    },
];
export function templateByKey(key) {
    return TEMPLATES.find((t) => t.key === key);
}
/** Blöcke einer Vorlagenseite in echte Blöcke mit Kennung umwandeln. */
export function blocksFromTemplate(page) {
    return page.blocks.map((b) => ({ id: blockId(), kind: b.kind, props: { ...BLOCKS[b.kind].defaults, ...(b.props ?? {}) } }));
}
// ---------------------------------------------------------------- Adressen
export const PAGE_SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,48}[a-z0-9])?$/;
/** Adressen, die schon für andere Zwecke belegt sind. */
export const RESERVED_PAGE_SLUGS = ["live", "player", "episodes", "hls", "media", "podcast.xml", "vorschau", "api", "internal",
    // Redaktion: Beitragsseiten, Beitragsliste und Sendungsseiten haben feste Adressen.
    "beitrag", "beitraege", "sendung",
    // Gewinnspiel: die Unterseite mit den Teilnahmebedingungen.
    "teilnahmebedingungen",
    // Private Veranstaltungen: die Gästeseite einer Feier und die Geschenkseite danach.
    "fest"];
export function isPageSlug(value) {
    return PAGE_SLUG_RE.test(value) && !RESERVED_PAGE_SLUGS.includes(value);
}
/** Aus einem Titel eine Adresse machen: „Über uns“ → „ueber-uns“. */
export function slugifyPage(title) {
    const s = title
        .toLowerCase()
        .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 50);
    return s || "seite";
}
